import { EMAIL_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, CopyButton, Dialog, DialogContent, Field, Icon, Input, Select, toast } from "@sagot/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "../../../lib/zod-resolver";
import type { ChannelOut } from "../shared/types";
import { ChannelCard, ChannelStatusBadge, ConversationsMetric } from "./channel-card";
import { useChannelErrorHandler, useChannelSwitch, type ChannelPageContext } from "./channel-context";
import { CHANNEL_ICON, emailDetail, type EmailDetail, type ReplyMode } from "./channels-model";
import { useConfigureEmail } from "./use-channels";

const P = "deployKnowledge.channels.email";

interface CardProps {
  channel: ChannelOut;
  ctx: ChannelPageContext;
}

interface SettingsDialogProps extends CardProps {
  detail: EmailDetail;
  onClose: () => void;
}

function EmailSettingsDialog({ channel, ctx, detail, onClose }: SettingsDialogProps) {
  const { t } = useTranslation();
  const configure = useConfigureEmail(ctx.agentId);
  const onError = useChannelErrorHandler(ctx);
  const schema = z.object({
    forwarding: z.union([z.literal(""), z.string().trim().max(EMAIL_MAX_LENGTH).email(t(`${P}.forwardingInvalid`))]),
  });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { forwarding: detail.forwardingAddress ?? "" } });
  const submit = form.handleSubmit(({ forwarding }) => {
    configure.mutate(
      { enabled: detail.configured ? channel.enabled : true, replyMode: detail.replyMode, forwardingAddress: forwarding.trim() || null },
      {
        onSuccess: () => {
          toast.success(t(`${P}.saved`));
          onClose();
        },
        onError: onError("email"),
      },
    );
  });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${P}.settingsTitle`)}
        description={t(`${P}.settingsBody`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form="email-settings-form" loading={configure.isPending}>
              {t(`${P}.save`)}
            </Button>
          </>
        }
      >
        <form id="email-settings-form" noValidate onSubmit={(event) => void submit(event)}>
          <Field label={t(`${P}.forwarding`)} hint={t(`${P}.forwardingHint`)} error={form.formState.errors.forwarding?.message}>
            <Input type="email" autoComplete="email" placeholder={t(`${P}.forwardingPlaceholder`)} {...form.register("forwarding")} />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InboundAddress({ address }: { address: string }) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 flex gap-2">
      <Input className="h-8 font-mono text-[11px]" readOnly value={address} aria-label={t(`${P}.inboundLabel`)} />
      <CopyButton value={address} label={t(`${P}.copyInbound`)} copiedLabel={t(`${P}.copied`)} className="btn-secondary" />
    </div>
  );
}

/** Email inbox: inbound address + copy, your mailbox, reply mode (draft for review / auto-reply). */
export function EmailCard({ channel, ctx }: CardProps) {
  const { t } = useTranslation();
  const toggle = useChannelSwitch(ctx);
  const configure = useConfigureEmail(ctx.agentId);
  const onError = useChannelErrorHandler(ctx);
  const detail = emailDetail(channel);
  const [editing, setEditing] = useState(false);
  const setMode = (replyMode: ReplyMode) => {
    configure.mutate({ enabled: channel.enabled, replyMode, forwardingAddress: detail.forwardingAddress }, {
      onSuccess: () => toast.success(t(`${P}.modeSaved`, { mode: t(replyMode === "auto" ? `${P}.modeAuto` : `${P}.modeDraft`) })),
      onError: onError("email"),
    });
  };
  const switchProps = detail.configured ? toggle(channel) : toggle(channel, { onChange: () => { setEditing(true); } });
  const footer = detail.configured ? (
    <>
      <ConversationsMetric count={channel.conversations_7d} emails />
      <Select
        className="h-8 w-36 text-xs"
        aria-label={t(`${P}.replyMode`)}
        value={configure.isPending ? configure.variables.replyMode : detail.replyMode}
        disabled={!ctx.canManage || configure.isPending}
        onChange={(event) => { setMode(event.target.value === "auto" ? "auto" : "draft"); }}
      >
        <option value="draft">{t(`${P}.modeDraft`)}</option>
        <option value="auto">{t(`${P}.modeAuto`)}</option>
      </Select>
    </>
  ) : (
    <>
      <ConversationsMetric count={channel.conversations_7d} emails />
      <Button size="sm" disabled={!ctx.canManage} onClick={() => { setEditing(true); }}>
        <Icon name="plus" />
        {t(`${P}.setUp`)}
      </Button>
    </>
  );
  return (
    <>
      <ChannelCard
        testId="channel-email"
        icon={CHANNEL_ICON.email}
        muted={!detail.configured}
        title={t("deployKnowledge.channels.names.email")}
        toggle={switchProps}
        status={<ChannelStatusBadge channel={channel} />}
        meta={
          detail.forwardingAddress ?? (detail.configured && ctx.canManage ? (
            <button type="button" className="font-semibold text-primary-ink underline-offset-2 hover:underline" onClick={() => { setEditing(true); }}>
              {t(`${P}.settings`)}
            </button>
          ) : undefined)
        }
        description={t(`${P}.description`)}
        footer={footer}
      >
        {detail.inboundAddress ? <InboundAddress address={detail.inboundAddress} /> : null}
      </ChannelCard>
      {editing ? <EmailSettingsDialog channel={channel} ctx={ctx} detail={detail} onClose={() => { setEditing(false); }} /> : null}
    </>
  );
}
